import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Music, Trash2, Upload, Loader2, GripVertical } from "lucide-react";

const CHUNK_SIZE = 1024 * 1024;

const PAGES = [
  { label: "Profile", path: "/profile" }, { label: "Book", path: "/book" },
  { label: "Training", path: "/training" }, { label: "League", path: "/league" },
  { label: "Community", path: "/community" }, { label: "Vault", path: "/vault" },
  { label: "Card Wars", path: "/card-wars" }, { label: "Tournaments", path: "/tournaments" },
  { label: "Pro Shop", path: "/store" }, { label: "Events", path: "/events" },
];

export default function SoundtrackManager() {
  const [tracks, setTracks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/music/tracks").then(({ data }) => setTracks(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(mp3|wav)$/i.test(file.name)) return toast.error("Upload an MP3 or WAV file");
    setUploading(true);
    setProgress(0);
    try {
      const uploadId = `music_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let res = null;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        res = (await api.post("/music/upload/chunk", fd)).data;
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success(`"${res.title}" added to the site soundtrack 🎵`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.title}" from the soundtrack?`)) return;
    try {
      await api.delete(`/music/tracks/${t.id}`);
      toast.success("Track removed");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const dropOn = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const next = [...tracks];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setTracks(next);
    setDragIdx(null);
    try {
      await api.put("/music/tracks/order", { ids: next.map((t) => t.id) });
      toast.success("Playlist order saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const togglePage = async (t, path) => {
    const pages = (t.pages || []).includes(path)
      ? (t.pages || []).filter((p) => p !== path)
      : [...(t.pages || []), path];
    try {
      await api.patch(`/music/tracks/${t.id}`, { pages });
      setTracks((ts) => ts.map((x) => x.id === t.id ? { ...x, pages } : x));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const clearPages = async (t) => {
    try {
      await api.patch(`/music/tracks/${t.id}`, { pages: [] });
      setTracks((ts) => ts.map((x) => x.id === t.id ? { ...x, pages: [] } : x));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="soundtrack-manager">
      <p className="text-xs text-zinc-500 mb-4">Every member hears these tracks through the music icon in the top bar — the playlist loops continuously while the app is open. MP3 or WAV, up to 60MB each. Assign a track to specific pages and it takes over the soundtrack there (battle music in Card Wars, chill vibes in the Vault); tracks set to Everywhere play on all other pages. Drag tracks by the grip to set the exact play order.</p>
      <input ref={fileRef} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" className="hidden"
        data-testid="soundtrack-file-input" onChange={(e) => upload(e.target.files?.[0])} />
      <button data-testid="soundtrack-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
        className="flex items-center gap-2 bg-[#A3E635] text-black font-black uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50 mb-4">
        {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</> : <><Upload className="w-4 h-4" /> Upload Track</>}
      </button>
      {tracks.length === 0 ? (
        <div className="text-sm text-zinc-600 border border-dashed border-pb-border p-5 text-center" data-testid="soundtrack-empty">
          No tracks yet — upload the first song and the player appears for everyone.
        </div>
      ) : (
        <div className="space-y-2" data-testid="soundtrack-list">
          {tracks.map((t, i) => (
            <div key={t.id} draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(i)}
              onDragEnd={() => setDragIdx(null)}
              className={`bg-pb-elevated/50 px-3 py-2 transition-all ${dragIdx === i ? "opacity-40 border border-dashed border-[#A3E635]" : "border border-transparent"}`}
              data-testid={`soundtrack-row-${t.id}`}>
              <div className="flex items-center gap-3">
                <GripVertical className="w-3.5 h-3.5 text-zinc-600 shrink-0 cursor-grab active:cursor-grabbing" data-testid={`soundtrack-grip-${t.id}`} />
                <Music className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />
                <span className="flex-1 text-sm font-semibold truncate">{i + 1}. {t.title}</span>
                <button data-testid={`soundtrack-delete-${t.id}`} onClick={() => remove(t)}
                  className="text-zinc-600 hover:text-pb-red transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`soundtrack-pages-${t.id}`}>
                <button data-testid={`soundtrack-page-everywhere-${t.id}`} onClick={() => clearPages(t)}
                  className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${!(t.pages || []).length ? "bg-[#A3E635] text-black border-[#A3E635]" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                  Everywhere
                </button>
                {PAGES.map((p) => (
                  <button key={p.path} data-testid={`soundtrack-page-${t.id}-${p.path.replace(/\//g, "") || "profile"}`}
                    onClick={() => togglePage(t, p.path)}
                    className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${(t.pages || []).includes(p.path) ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
